import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import NumberFormat, { NumberFormatProps } from 'react-number-format'

import { CurrencyCode } from '@cash-web/protos/squareup/common/currency_codes.pb'
import { convertDecimalMoneyToCents } from '@cash-web/shared-util-formatting'

import { formatMoney } from '../utils/formatMoney'

export type GenericMoneyInputProps = {
  ['data-testid']?: string
  ariaLabel?: string
  currencyCode?: CurrencyCode
  currencyPrefix?: string
  forceDecimalPoint?: boolean
  formattedValue: string
  isRequired?: boolean
  maximumAmount?: number
  minimumAmount?: number
  name: string
  placeholder?: string
  setFormattedValue: (value: string) => void
  shouldAutoFocus?: boolean
  validationErrorMessages?: {
    overMaximumAmount?: string
  }
  decimalScale?: 0 | 2
} & Omit<NumberFormatProps, 'decimalScale'>

export const GenericMoneyInput = ({
  ariaLabel,
  currencyCode = CurrencyCode.USD,
  customInput,
  forceDecimalPoint,
  formattedValue,
  isRequired = true,
  maximumAmount,
  minimumAmount,
  name,
  placeholder,
  setFormattedValue,
  shouldAutoFocus,
  validationErrorMessages,
  decimalScale,
  ...props
}: GenericMoneyInputProps) => {
  const { t } = useTranslation()
  const { setValue, register, formState } = useFormContext()
  const { errors, dirtyFields, isSubmitted } = formState

  const defaultPlaceholder = decimalScale === 0 ? '0' : '0.00'

  const { onBlur, ...inputProps } = register(name, {
    required: {
      value: isRequired,
      message: t('Amount is required.', { context: 'Error message: amount not specified' }),
    },
    validate: (value: string) => {
      const valueAsNumber = Number(value)
      if (value != null && Number.isNaN(valueAsNumber)) {
        return t('Amount must be a number.', {
          context: 'Error message: amount is not a number',
        })
      }
      if (minimumAmount != null && valueAsNumber < minimumAmount) {
        return t('Amount must be at least {amount}.', {
          amount: formatMoney({ amount: minimumAmount, currency_code: currencyCode }),
          context: 'Error message: amount is less than minimum allowed amount',
        })
      }
      if (maximumAmount != null && valueAsNumber > maximumAmount) {
        return (
          validationErrorMessages?.overMaximumAmount ??
          t('Amount must be less than or equal to {amount}.', {
            amount: formatMoney({ amount: maximumAmount, currency_code: currencyCode }),
            context: 'Error message: amount is more than maximum allowed amount',
          })
        )
      }
      return true
    },
  })

  const error = errors[name]
  const showError = error && (error?.type !== 'required' || isSubmitted)

  return (
    <NumberFormat
      {...props}
      customInput={customInput}
      decimalScale={decimalScale ?? 2}
      // enforce two decimal places once user has shown intention to add cents
      fixedDecimalScale={forceDecimalPoint || formattedValue.includes('.')}
      placeholder={placeholder || defaultPlaceholder}
      value={formattedValue}
      allowNegative={false}
      thousandSeparator={true}
      onValueChange={target => {
        setFormattedValue(target.formattedValue)
        const value = target.value === '' ? '' : convertDecimalMoneyToCents({ decimalAmount: target.value })
        setValue(name, value)
      }}
      {...inputProps}
      onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
        if (dirtyFields[name]) {
          onBlur(e)
        }
      }}
      autoComplete="off"
      autoFocus={shouldAutoFocus}
      aria-label={ariaLabel}
      className={showError ? 'error' : ''}
      data-testid={props['data-testid']}
    />
  )
}
